import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import { Avatar, Badge, Text } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { TenantName } from '../../../../components/TenantName';
import { TwoLineCell } from '../../../../components/TwoLineCell';
import { useTenantMap } from '../../../../data/useTenants';
import { formatDate, formatDateTime, formatRelative } from '../../../../lib/format';
import type { Indexed } from '../../../../lib/types';
import { useT } from '../../../../i18n/I18nProvider';
import { OUTCOME_COLOR, outcomeOf, type Delivery } from './deliveryState';

const MUTED = 'var(--rr-color-text-muted)';

export function DeliveriesTable({ rows, loading, endpointFor, onRowClick }: {
  rows: Delivery[];
  loading: boolean;
  /** The subscription's URL, resolved from the cached subscriptions list. */
  endpointFor: (subscriptionId: string) => string | undefined;
  onRowClick: (row: Delivery) => void;
}) {
  const t = useT();
  const { map: tenants } = useTenantMap();

  return (
    <DataTable<Indexed<Delivery>>
      records={rows as Indexed<Delivery>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('webhooks.deliveries.tableEmpty')}
      onRowClick={onRowClick}
      columns={[
        {
          accessor: 'scheduled_at',
          title: t('webhooks.deliveries.columns.scheduled'),
          sortable: true,
          render: (r) => (
            <span title={formatDateTime(r.scheduled_at)}>
              <TwoLineCell primary={formatDate(r.scheduled_at)} secondary={formatRelative(r.scheduled_at)} />
            </span>
          )
        },
        {
          accessor: 'attempted_at',
          title: t('webhooks.deliveries.columns.attempted'),
          sortable: true,
          // A pending row's age is unknowable without this: `scheduled_at` says
          // when it was queued, not when it was last tried.
          render: (r) => r.attempted_at
            ? <span title={formatDateTime(r.attempted_at)}>
                <TwoLineCell primary={formatRelative(r.attempted_at)} secondary={formatDate(r.attempted_at)} />
              </span>
            : <Text role="caption" c={MUTED}>{t('webhooks.deliveries.neverAttempted')}</Text>
        },
        {
          accessor: 'organisation_id',
          title: t('webhooks.deliveries.columns.tenant'),
          render: (r) => {
            const tenant = tenants.get(r.organisation_id);
            return (
              <TwoLineCell
                leading={<Avatar name={tenant?.name ?? '?'} size={26} />}
                primary={<TenantName id={r.organisation_id} />}
                secondary={tenant?.slug}
              />
            );
          }
        },
        { accessor: 'event_type', title: t('webhooks.deliveries.columns.event'), render: (r) => <code>{r.event_type}</code> },
        { accessor: 'attempt_number', title: t('webhooks.deliveries.columns.attempt'), textAlign: 'right' },
        {
          accessor: 'http_status',
          title: t('webhooks.deliveries.columns.http'),
          render: (r) => {
            const outcome = outcomeOf(r);
            const badge = (
              <Badge size="xs" color={OUTCOME_COLOR[outcome]} variant="light">
                {outcome === 'blocked'
                  ? t('webhooks.deliveries.blocked')
                  : (r.http_status ?? t('status.pending'))}
              </Badge>
            );
            return outcome === 'blocked'
              ? <Tooltip label={t('webhooks.deliveries.blockedReason', { reason: r.blocked_reason ?? '' })} position="top" maxWidth={360}>{badge}</Tooltip>
              : badge;
          }
        },
        {
          accessor: 'latency_ms',
          title: t('webhooks.deliveries.columns.latency'),
          textAlign: 'right',
          // A null latency used to render as the string " ms", on exactly the
          // blocked and pending rows this page most needs to read cleanly.
          render: (r) => r.latency_ms == null
            ? <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>
            : t('webhooks.deliveries.latencyMs', { value: r.latency_ms })
        },
        {
          accessor: 'subscription_id',
          title: t('webhooks.deliveries.columns.endpoint'),
          // The delivery row carries only a subscription id, which is a join
          // key rather than something to read. The URL comes from the
          // subscriptions list this page already has in cache.
          render: (r) => {
            const url = endpointFor(r.subscription_id);
            if (!url) return <Text role="caption" c={MUTED}>{t('webhooks.deliveries.unknownEndpoint')}</Text>;
            let host = url;
            let path = '';
            try {
              const parsed = new URL(url);
              host = parsed.host;
              path = parsed.pathname;
            } catch {
              // A URL the service accepted but the browser cannot parse still
              // reads better whole than as an empty cell.
            }
            return (
              <Tooltip label={url} position="top" maxWidth={520}>
                <span><TwoLineCell primary={host} secondary={path} /></span>
              </Tooltip>
            );
          }
        }
      ]}
    />
  );
}
